import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getIos, deleteIo, ApiError } from "../api.js";
import { STATUS_MAP } from "./StatusBadge.jsx";
import ConfidenceIndicator from "./ConfidenceIndicator.jsx";
import { loadUploadedIos, onUploadedIosChanged, removeUploadedIo } from "../lib/localIos.js";
import { changeIoStatus, STATUS_OPTIONS } from "../lib/statusActions.js";

// Available models for the dropdown. Fetched from the parser service /models
// endpoint at runtime. Falls back to this static list if the endpoint is
// unavailable (e.g. parser service not running in dev).
const FALLBACK_MODELS = [
  { value: "", label: "Default (auto)", supports_vision: true, vision_warning: null },
  // Anthropic Claude — all support vision and are good at handwriting
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (default)", supports_vision: true, vision_warning: null },
  { value: "claude-opus-4-6", label: "Claude Opus 4.6 (higher quality)", supports_vision: true, vision_warning: null },
  { value: "claude-haiku-4-6", label: "Claude Haiku 4.6 (faster, cheaper)", supports_vision: true, vision_warning: null },
  { value: "claude-sonnet-4-5", label: "Claude Sonnet 4.5 (previous gen)", supports_vision: true, vision_warning: null },
  // Ollama cloud — vision support varies
  { value: "glm-5.2", label: "GLM-5.2 Cloud", supports_vision: false, vision_warning: "Does not support image input — will reject image files" },
  { value: "gemma4:31b", label: "Gemma4 31B Cloud", supports_vision: true, vision_warning: "Poor handwriting accuracy — may misread multiple words" },
  { value: "minimax-m3", label: "MiniMax M3 Cloud", supports_vision: true, vision_warning: "Poor handwriting accuracy — may misread multiple words" },
  { value: "kimi-k2.6", label: "Kimi K2.6 Cloud", supports_vision: true, vision_warning: "High latency (~36s) and unreliable output for image tasks" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(n) {
  if (n == null || n === "") return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

// Worst-case (minimum) confidence score across every field the LLM scored.
function aggregateConfidence(io) {
  const conf = io?._meta?.confidence;
  if (!conf || typeof conf !== "object") return null;
  const scores = Object.values(conf).filter((v) => typeof v === "number");
  return scores.length ? Math.min(...scores) : null;
}

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const navigate = useNavigate();

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [advertiserSearch, setAdvertiserSearch] = useState("");
  const [agencySearch, setAgencySearch] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  // Flight date range — OFF by default. A default range silently hides IOs
  // whose flight dates fall outside today's window, which looks exactly like
  // "the dashboard is broken". Empty means "no date filtering".
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Data + UI state
  const [ios, setIos] = useState([]);
  const [total, setTotal] = useState(0);
  // IOs extracted via Upload but not yet persisted to the backend — kept in
  // localStorage as a stopgap (see lib/localIos.js) and merged in below.
  const [localIos, setLocalIos] = useState(loadUploadedIos);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [changingStatusIds, setChangingStatusIds] = useState(new Set());

  // Model selection — persisted to localStorage so it survives page reloads.
  const [models, setModels] = useState(FALLBACK_MODELS);
  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem("longship.selectedModel") || "";
  });

  // Fetch available models from the parser service on mount.
  useEffect(() => {
    const parserUrl = import.meta.env.VITE_PARSER_URL || "";
    const url = parserUrl ? `${parserUrl}/models` : "/parser/models";
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.models?.length) {
          // Prepend a "default" option. Preserve vision metadata from the API.
          setModels([
            { value: "", label: "Default (auto)", supports_vision: true, vision_warning: null },
            ...data.models.map((m) => ({
              value: m.value,
              label: m.label,
              supports_vision: m.supports_vision ?? true,
              vision_warning: m.vision_warning ?? null,
            })),
          ]);
        }
      })
      .catch(() => {
        // Parser service not running — use fallback models.
      });
  }, []);

  // Persist model selection.
  useEffect(() => {
    localStorage.setItem("longship.selectedModel", selectedModel);
  }, [selectedModel]);

  // Re-read local IOs when the Upload page (or another tab) adds one.
  useEffect(() => {
    const unsubscribe = onUploadedIosChanged(() => setLocalIos(loadUploadedIos()));
    return unsubscribe;
  }, []);

  // -------------------------------------------------------------------------
  // Fetch IOs whenever filters/pagination change.
  // -------------------------------------------------------------------------
  const fetchIos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = {
        page,
        limit: PAGE_SIZE,
      };
      if (statusFilter) query.status = statusFilter;
      if (advertiserSearch) query.advertiser = advertiserSearch;
      if (agencySearch) query.agency = agencySearch;
      const res = await getIos(query);
      // getIos() unwraps the API's { data } envelope: the list endpoint comes
      // back as a bare array, with total on the sibling property. Handle both
      // shapes so a server change can't silently blank the table.
      const rows = Array.isArray(res) ? res : (res?.data ?? []);
      setIos(rows);
      setTotal(Array.isArray(res) ? rows.length : (res?.total ?? rows.length));
    } catch (err) {
      setError(`Failed to load IOs: ${err instanceof ApiError ? err.message : String(err)}`);
      setIos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, advertiserSearch, agencySearch]);

  useEffect(() => {
    fetchIos();
  }, [page, statusFilter, advertiserSearch, agencySearch]);

  // -------------------------------------------------------------------------
  // Client-side post-filter (budget range, date range) + sort.
  // The API supports status/advertiser/agency/pagination; budget+date range
  // filtering are applied client-side on the current page for the MVP.
  // -------------------------------------------------------------------------
  const filtered = useMemo(() => {
    // Local (upload-only) records live in this browser's localStorage and are
    // a legacy stopgap — the API persists uploaded IOs now, so these duplicate
    // real rows with a "LOCAL" badge. Hidden from the Dashboard so the table
    // only shows platform records; set SHOW_LOCAL_IOS to true to bring them
    // back for debugging.
    const SHOW_LOCAL_IOS = false;
    let localRows = SHOW_LOCAL_IOS ? localIos : [];
    if (statusFilter) localRows = localRows.filter((r) => (r.status || "") === statusFilter);
    if (advertiserSearch) {
      const q = advertiserSearch.toLowerCase();
      localRows = localRows.filter((r) => (r.advertiser_name || "").toLowerCase().includes(q));
    }
    if (agencySearch) {
      const q = agencySearch.toLowerCase();
      localRows = localRows.filter((r) => (r.agency || "").toLowerCase().includes(q));
    }

    let rows = page === 1 ? [...localRows, ...ios] : ios;
    if (budgetMin !== "") {
      const min = Number(budgetMin);
      if (!Number.isNaN(min)) rows = rows.filter((r) => Number(r.total_gross_budget) >= min);
    }
    if (budgetMax !== "") {
      const max = Number(budgetMax);
      if (!Number.isNaN(max)) rows = rows.filter((r) => Number(r.total_gross_budget) <= max);
    }
    if (dateFrom) {
      rows = rows.filter((r) => (r.contract_start_date || "") >= dateFrom);
    }
    if (dateTo) {
      rows = rows.filter((r) => (r.contract_end_date || "") <= dateTo);
    }
    // Sort
    rows = [...rows].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === "created_at" || sortKey === "createdAt") {
        av = new Date(a.createdAt || a.created_at || 0).getTime();
        bv = new Date(b.createdAt || b.created_at || 0).getTime();
      } else if (sortKey === "confidence") {
        av = aggregateConfidence(a) ?? -1;
        bv = aggregateConfidence(b) ?? -1;
      } else if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [ios, localIos, page, statusFilter, advertiserSearch, agencySearch, budgetMin, budgetMax, dateFrom, dateTo, sortKey, sortDir]);

  // True when any filter is narrowing the table. Drives the "hidden by
  // filters" empty state and the Clear filters button.
  const hasActiveFilters = Boolean(
    statusFilter || advertiserSearch || agencySearch || budgetMin || budgetMax || dateFrom || dateTo
  );

  function clearFilters() {
    setStatusFilter("");
    setAdvertiserSearch("");
    setAgencySearch("");
    setBudgetMin("");
    setBudgetMax("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  async function handleDelete(io) {
    const label = io.io_number || io.id;
    if (!window.confirm(`Remove ${label}? This can't be undone.`)) return;

    if (io.source === "upload") {
      // Local-only record — just drop it from this browser's storage.
      removeUploadedIo(io.id);
      return;
    }

    setDeletingIds((prev) => new Set(prev).add(io.id));
    setError(null);
    try {
      await deleteIo(io.id);
      fetchIos();
    } catch (err) {
      setError(`Failed to remove ${label}: ${err instanceof ApiError ? err.message : String(err)}`);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(io.id);
        return next;
      });
    }
  }

  async function handleStatusChange(io, newStatus) {
    setChangingStatusIds((prev) => new Set(prev).add(io.id));
    setError(null);
    try {
      const applied = await changeIoStatus(io, newStatus);
      if (!applied) return; // user cancelled (e.g. the reject-reason prompt)
      if (io.source === "upload") setLocalIos(loadUploadedIos());
      else fetchIos();
    } catch (err) {
      setError(`Failed to change status for ${io.io_number || io.id}: ${err instanceof ApiError ? err.message : String(err)}`);
    } finally {
      setChangingStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(io.id);
        return next;
      });
    }
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function rowClick(id) {
    navigate(`/ios/${encodeURIComponent(id)}`);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="dashboard container">
      <div className="dashboard-header">
        <h1>Insertion Orders</h1>
        <div className="dashboard-actions">
          <label className="model-selector">
            <span className="model-selector-label">AI Model</span>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              title="Select which AI model to use for IO extraction"
            >
              {models.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            {(() => {
              const m = models.find((m) => m.value === selectedModel);
              if (!m?.vision_warning) return null;
              const isBlocker = m.supports_vision === false;
              return (
                <span className={`model-vision-warning ${isBlocker ? "model-vision-warning-blocker" : ""}`} title={m.vision_warning}>
                  ⚠ {isBlocker ? "⛔ " : ""}{m.vision_warning}
                </span>
              );
            })()}
          </label>
          <button type="button" className="btn btn-primary" onClick={() => navigate("/upload")} title="Upload a new IO">
            + New IO
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <label className="filter-field">
          <span>Status</span>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {Object.entries(STATUS_MAP).map(([key, meta]) => (
              <option key={key} value={key === "draft" ? "draft" : key}>
                {meta.label}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Advertiser</span>
          <input
            type="text"
            placeholder="Search advertiser…"
            value={advertiserSearch}
            onChange={(e) => { setAdvertiserSearch(e.target.value); setPage(1); }}
          />
        </label>
        <label className="filter-field">
          <span>Agency</span>
          <input
            type="text"
            placeholder="Search agency…"
            value={agencySearch}
            onChange={(e) => { setAgencySearch(e.target.value); setPage(1); }}
          />
        </label>
        <label className="filter-field">
          <span>Flight from</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="filter-field">
          <span>Flight to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="filter-field">
          <span>Budget min</span>
          <input type="number" placeholder="0" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} />
        </label>
        <label className="filter-field">
          <span>Budget max</span>
          <input type="number" placeholder="∞" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} />
        </label>
        {hasActiveFilters && (
          <button
            type="button"
            className="btn btn-sm btn-ghost dashboard-clear-filters"
            onClick={clearFilters}
            title="Reset all filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Error */}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Table */}
      <div className="dashboard-table-wrap">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("io_number")}>IO #</th>
              <th className="sortable" onClick={() => handleSort("advertiser_name")}>Advertiser</th>
              <th className="sortable" onClick={() => handleSort("agency")}>Agency</th>
              <th className="sortable" onClick={() => handleSort("status")}>Status</th>
              <th className="sortable num" onClick={() => handleSort("confidence")}>Confidence</th>
              <th className="sortable num" onClick={() => handleSort("total_gross_budget")}>Gross Budget</th>
              <th className="num">Line Items</th>
              <th className="sortable" onClick={() => handleSort("created_at")}>Created</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="table-loading">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="table-empty">
                  {hasActiveFilters ? (
                    <>
                      No IOs match the current filters.
                      {ios.length > 0 && (
                        <>
                          {" "}
                          {ios.length} IO{ios.length === 1 ? "" : "s"} hidden by filters.{" "}
                          <button type="button" className="btn-link" onClick={clearFilters}>
                            Clear filters
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      No IOs yet.{" "}
                      <button type="button" className="btn-link" onClick={() => navigate("/upload")}>
                        Upload an IO
                      </button>{" "}
                      to get started.
                    </>
                  )}
                </td>
              </tr>
            )}
            {!loading && filtered.map((io) => {
              const isLocal = io.source === "upload";
              const confidenceScore = aggregateConfidence(io);
              return (
                <tr
                  key={io.id}
                  className={`dashboard-row${isLocal ? " dashboard-row-local" : ""}`}
                  onClick={() => rowClick(io.id)}
                  title={isLocal ? "Stored locally only (this browser) — not yet submitted to the platform" : undefined}
                >
                  <td className="mono">
                    {io.io_number || io.id}
                    {isLocal && (
                      <span className="status-badge badge-gray local-badge" title="Extracted via Upload — stored in this browser only">
                        LOCAL
                      </span>
                    )}
                  </td>
                  <td>{io.advertiser_name}</td>
                  <td>{io.agency}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className={`status-select status-badge ${STATUS_MAP[io.status]?.className || "badge-gray"}`}
                      value={io.status}
                      onChange={(e) => handleStatusChange(io, e.target.value)}
                      disabled={changingStatusIds.has(io.id)}
                      title="Click to change status"
                    >
                      <option value={io.status}>
                        {changingStatusIds.has(io.id) ? "Changing…" : (STATUS_MAP[io.status]?.label || io.status)}
                      </option>
                      {STATUS_OPTIONS.filter((o) => o.value !== io.status).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="num">
                    {confidenceScore == null ? (
                      <span className="muted">—</span>
                    ) : (
                      <span className="confidence-cell">
                        <ConfidenceIndicator confidence={confidenceScore} />
                        <span className="mono">{confidenceScore.toFixed(2)}</span>
                      </span>
                    )}
                  </td>
                  <td className="num">{formatCurrency(io.total_gross_budget)}</td>
                  <td className="num">{(io.line_items || []).length}</td>
                  <td className="muted">{formatDate(io.createdAt || io.created_at)}</td>
                  <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-danger-outline btn-sm"
                      disabled={deletingIds.has(io.id)}
                      onClick={() => handleDelete(io)}
                      title={isLocal ? "Remove this local upload" : "Remove this IO"}
                    >
                      {deletingIds.has(io.id) ? "Removing…" : "Remove"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="dashboard-pagination">
        <span className="pagination-info">
          Page {page} of {totalPages} · {total} IOs
          {page === 1 && localIos.length > 0 && ` · ${localIos.length} local (not yet submitted)`}
        </span>
        <div className="pagination-controls">
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage(1)}>
            «
          </button>
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ‹
          </button>
          <span className="pagination-current">{page}</span>
          <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            ›
          </button>
          <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
            »
          </button>
        </div>
      </div>
    </div>
  );
}